/**
 * ConversionBars Component
 * Displays conversion rates by credit type
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/shared/components/ui";
import { TrendingUp } from "lucide-react";
import { getCreditTypeLabel } from "@/shared/lib/utils";
import type { CreditType } from "@/shared/types";

interface ConversionData {
  type: CreditType;
  leads: number;
  completed: number;
  rate: number;
}

interface ConversionBarsProps {
  data: ConversionData[];
  isLoading?: boolean;
}

export function ConversionBars({ data, isLoading = false }: ConversionBarsProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Konwersja per typ kredytu
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-24 mb-2" />
                <div className="h-6 bg-gray-200 rounded" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Konwersja per typ kredytu
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-500 text-center py-4">
            Brak danych konwersji
          </p>
        </CardContent>
      </Card>
    );
  }

  // Sort by rate descending
  const sortedData = [...data].sort((a, b) => b.rate - a.rate);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5" />
          Konwersja per typ kredytu
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {sortedData.map((item) => (
            <div key={item.type}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm font-medium text-gray-700">
                  {getCreditTypeLabel(item.type)}
                </span>
                <span className="text-sm text-gray-500">
                  {item.completed}/{item.leads} leadów
                </span>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      item.rate >= 50
                        ? "bg-success-500"
                        : item.rate >= 30
                        ? "bg-warning-500"
                        : "bg-error-400"
                    }`}
                    style={{ width: `${item.rate}%` }}
                  />
                </div>
                <span className="text-sm font-semibold text-gray-900 w-12 text-right">
                  {item.rate.toFixed(0)}%
                </span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
